import { type ReactElement, useState } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { Icon, UnseenCue } from '../index';

import type { useTheme } from './theme';
import type { makeStyles } from './loggerStyles';
import type { EditingTarget } from './LoggerScreen';
import { WorkoutNotesPanel } from './WorkoutNotesPanel';
import { hashNotesContent, useNotesRead } from './useNotesRead';

// Logger header — title-left + 4-icon right cluster (↗ share / ⌄ minimize
// / 💾 save / ✕ close). Extracted verbatim out of LoggerScreen (Track R).
// Presentational: every action routes back through the passed handlers;
// the only inline logic is the canShare gate, moved verbatim with the
// IIFE it lived in. postSaveSummary mode reuses the cluster minus save +
// with the close icon acting as Done.
type LoggerHeaderProps = {
  postSaveSummary: { sessionId: string; onDone: () => void } | null;
  editing: EditingTarget | null;
  elapsedLabel: string;
  hasSessionResult: boolean;
  headerSharing: boolean;
  submitting: boolean;
  entriesLength: number;
  onMinimize?: () => void;
  onSave: () => void;
  onCancel: () => void;
  triggerHeaderShare: () => void;
  /** Arm a rest countdown on demand (header clock, live logging only). */
  onStartRest?: () => void;
  /** The active exercise is part of a superset. The clock lights up then
   *  (MX-5 D9): inside a group the automatic rest is the 4s implement change,
   *  so "start a real rest" is the only moment that affordance answers a
   *  question the athlete is actually asking. */
  inSuperset?: boolean;
  /** Read-only coach context for the workout-level "?" panel (program
   *  description + the day's session note). Absent for ad-hoc/edit sessions
   *  → the "?" button isn't shown. */
  coachNotes?: { programDescription?: string; sessionNote?: string };
  styles: ReturnType<typeof makeStyles>;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

export function LoggerHeader({
  postSaveSummary,
  editing,
  elapsedLabel,
  hasSessionResult,
  headerSharing,
  submitting,
  entriesLength,
  onMinimize,
  onSave,
  onCancel,
  triggerHeaderShare,
  onStartRest,
  inSuperset = false,
  coachNotes,
  styles,
  theme,
  t,
}: LoggerHeaderProps): ReactElement {
  const hasCoachNotes = Boolean(
    coachNotes &&
      (coachNotes.programDescription || coachNotes.sessionNote),
  );
  const [notesOpen, setNotesOpen] = useState(false);
  // Per-device read flag: pulse the "?" until the athlete opens it once.
  // Content-hashed so an edited coach note re-pulses.
  const contentKey = hasCoachNotes
    ? hashNotesContent([
        coachNotes?.programDescription,
        coachNotes?.sessionNote,
      ])
    : null;
  const { read, markRead } = useNotesRead(contentKey);
  // R7/R8 — the announcement is <UnseenCue>, shared with the ✕ that declares
  // gear missing: it breathes when motion is allowed and shows a static mark
  // when it is not. What stayed here is only WHEN this "?" counts as seen.
  const cueSeen = read || !hasCoachNotes;
  const onToggleNotes = () => {
    setNotesOpen((v) => !v);
    if (!read) markRead();
  };
  // Header = STATUS, not a title (Antenore 2026-06-06): the workout name is
  // edited in the form just below, so repeating it here is noise. Show an
  // "Edit" status only when editing a past session; a live new/repeat log
  // shows nothing but the elapsed timer. The "?" lives in the right cluster
  // next to ×, like a normal window's info affordance.
  const isTrueEdit = editing != null && editing.mode !== 'repeat';
  const titleStatus = postSaveSummary
    ? t('mobile.postSession.summaryTitle', { defaultValue: 'Workout saved' })
    : isTrueEdit
      ? t('mobile.logger.editTitle')
      : null;
  const showElapsed = !isTrueEdit && !postSaveSummary;
  return (
    <View>
      <View style={styles.header}>
      <View style={styles.titleRow}>
        <View style={styles.titleCol}>
          {titleStatus ? (
            <Text style={styles.title} numberOfLines={1}>
              {titleStatus}
            </Text>
          ) : null}
          {showElapsed ? (
            <Text
              style={styles.titleElapsed}
              numberOfLines={1}
              accessibilityLabel={t('mobile.logger.elapsedA11y', {
                defaultValue: 'Elapsed time',
              })}
            >
              {elapsedLabel}
            </Text>
          ) : null}
        </View>
      </View>
      <View style={styles.rightActions}>
        {/* Share icon — always visible across all modes, brand-
            colored + actionable only when a SessionResult is loaded
            AND the workout is in a shareable state (edit or post-
            save). In new / repeat modes the icon is greyed: there's
            nothing on the server to share yet. Single Pressable
            covers all branches (new / repeat / edit / postSave). */}
        {(() => {
          // editing.mode is set explicitly only for 'repeat' (see
          // WorkoutsListScreen.tsx:538). Plain edit-from-history
          // creates the EditingTarget WITHOUT a mode field — the
          // hydrateFromWorkout default arg fills it as 'edit' at
          // read time but the prop itself is undefined here. So
          // treat "missing mode" as edit-mode for canShare gating.
          const isEditMode = editing != null && editing.mode !== 'repeat';
          const canShare =
            hasSessionResult && (postSaveSummary !== null || isEditMode);
          return (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('mobile.postSession.shareCta', {
                defaultValue: 'Share session card',
              })}
              onPress={() => {
                if (canShare) void triggerHeaderShare();
              }}
              disabled={!canShare || headerSharing}
              hitSlop={8}
              style={({ pressed }) => [
                styles.headerIconButton,
                pressed && styles.headerIconButtonPressed,
                (!canShare || headerSharing) && styles.actionDisabled,
              ]}
            >
              <Icon
                name="share"
                size={theme.typography.sizes.lg}
                color={canShare ? theme.palette.brand500 : theme.palette.ink}
              />
            </Pressable>
          );
        })()}
        {/* Manual rest-start clock — a LOGGER affordance, hidden while editing
            a past session (you're correcting history, not resting between sets;
            Antenore 2026-07-10 "cose proprie di un logger e non di un editor"). */}
        {postSaveSummary === null && !isTrueEdit && onStartRest ? (
          // ⚠️ Deliberately NOT <IconButton>, though it encodes exactly this
          // "lit" state. Migrated to it first, and the device said no: the
          // primitive's default variant is a BORDERED surface2 box, and every
          // other icon in this header is bare, so the clock came out as the one
          // boxed glyph in the row. A primitive that is right in the abstract
          // can still be wrong for the cluster it lands in.
          // The active tint below is IconButton's OWN token pair, not a new
          // one, so the two can't drift on colour. Migrating the whole header
          // cluster is the real fix and is registered in next/consolidation.md
          // — it is a header restyle, not a line of this slice.
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ selected: inSuperset }}
            accessibilityLabel={t('mobile.logger.startRestManual', {
              defaultValue: 'Start rest timer',
            })}
            onPress={onStartRest}
            hitSlop={8}
            style={({ pressed }) => [
              styles.headerIconButton,
              inSuperset && styles.headerIconButtonActive,
              pressed && styles.headerIconButtonPressed,
            ]}
          >
            <Icon
              name="clock"
              size={theme.typography.sizes.lg}
              color={theme.palette.ink}
            />
          </Pressable>
        ) : null}
        {onMinimize && !postSaveSummary ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('mobile.logger.minimize')}
            onPress={onMinimize}
            disabled={submitting}
            hitSlop={8}
            style={({ pressed }) => [
              styles.headerIconButton,
              pressed && styles.headerIconButtonPressed,
              submitting && styles.actionDisabled,
            ]}
          >
            <Icon
              name="chevron-down"
              size={theme.typography.sizes.lg}
              color={theme.palette.ink}
            />
          </Pressable>
        ) : null}
        {postSaveSummary === null ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('mobile.logger.saveWorkout', {
              defaultValue: 'Save workout',
            })}
            onPress={onSave}
            disabled={submitting || entriesLength === 0}
            hitSlop={8}
            style={({ pressed }) => [
              styles.headerIconButton,
              pressed && styles.headerIconButtonPressed,
              (submitting || entriesLength === 0) && styles.actionDisabled,
            ]}
          >
            {submitting ? (
              <ActivityIndicator color={theme.palette.brand500} />
            ) : (
              <Icon
                name="save"
                size={theme.typography.sizes.lg}
                color={theme.palette.brand500}
              />
            )}
          </Pressable>
        ) : null}
        {/* Coach-notes "?" — info affordance, right cluster next to ×
            (Antenore 2026-06-06). Unread = brand glyph + pulse (motion, so
            the state is never colour-only); opening marks it read. */}
        {hasCoachNotes ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('mobile.logger.notesToggle', {
              defaultValue: 'Coach notes for this workout',
            })}
            accessibilityState={{ expanded: notesOpen }}
            onPress={onToggleNotes}
            hitSlop={8}
            style={({ pressed }) => [
              styles.headerIconButton,
              pressed && styles.headerIconButtonPressed,
            ]}
          >
            <UnseenCue theme={theme} seen={cueSeen}>
              <Icon
                name="help"
                size={theme.typography.sizes.lg}
                color={
                  !read || notesOpen
                    ? theme.palette.brand500
                    : theme.palette.ink
                }
              />
            </UnseenCue>
          </Pressable>
        ) : null}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            postSaveSummary
              ? t('mobile.postSession.done', { defaultValue: 'Done' })
              : t('mobile.logger.closeWorkout', {
                  defaultValue: 'Close workout',
                })
          }
          onPress={postSaveSummary ? postSaveSummary.onDone : onCancel}
          disabled={submitting}
          hitSlop={8}
          style={({ pressed }) => [
            styles.headerIconButton,
            pressed && styles.headerIconButtonPressed,
            submitting && styles.actionDisabled,
          ]}
        >
          {/* headerIconGlyphDanger overrides headerIconGlyph color: p.ink → p.ink2 */}
          <Icon name="close" size={theme.typography.sizes.lg} color={theme.palette.ink2} />
        </Pressable>
      </View>
      </View>
      {hasCoachNotes && notesOpen && coachNotes ? (
        <WorkoutNotesPanel coachNotes={coachNotes} styles={styles} t={t} />
      ) : null}
    </View>
  );
}
